import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, TextField, MenuItem,
  Button, Box, IconButton
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchApi } from './fetchApi';


interface MetaResponse {
  quelle_typ: { code: string; name: string }[];
  ziel_typ: { code: string; name: string }[];
  lagerorte: { id: number; name: string }[];
  lieferanten: { id: number; name: string }[];
  externe_quellen: { code: string; name: string }[];
  externe_ziele: { code: string; name: string }[];
}

interface Artikel {
  id: number;
  parent_id: number | null;
  bezeichnung: string;
  farbe: string | null;
  groesse: string | null;
  sku: string;
}

interface Position {
  parent_id: number | '';
  child_id: number | '';
  menge: number | '';
  menge_verfuegbar: number;
}

export default function LieferscheinFormular() {
  const [meta, setMeta] = useState<MetaResponse | null>(null);

  const [parents, setParents] = useState<Artikel[]>([]);
  const [childs, setChilds] = useState<Artikel[]>([]);

  const [quelleTyp, setQuelleTyp] = useState('');
  const [quelleId, setQuelleId] = useState<number | null>(null);

  const [zielTyp, setZielTyp] = useState('');
  const [zielId, setZielId] = useState<number | null>(null);

  const [bemerkung, setBemerkung] = useState('');

  const [positionen, setPositionen] = useState<Position[]>([
    { parent_id: '', child_id: '', menge: '', menge_verfuegbar: 0 }
  ]);

  // ========================================================
  // DATEN LADEN
  // ========================================================
  useEffect(() => {
    async function lade() {
      try {
        const metaRes = await fetchApi('/api/lieferschein/meta');
        const metaJson = await metaRes.json();

        const artRes = await fetchApi('/api/artikel');
        const artikel = await artRes.json();

        const parents = artikel.filter((a: Artikel) => a.parent_id === null);
        const childs = artikel.filter((a: Artikel) => a.parent_id !== null);

        setMeta(metaJson);
        setParents(parents);
        setChilds(childs);
      } catch (err: any) {
  console.error('Fehler beim Laden:', err);
  if (err?.message === '401') {
    alert('Session abgelaufen. Bitte neu einloggen.');
  }
}

    }

    lade();
  }, []);

  // ========================================================
  // POSITIONEN-LOGIK
  // ========================================================
  const handlePositionsChange = async (
    index: number,
    field: keyof Position,
    value: any
  ) => {
    const updated = [...positionen];
    updated[index][field] = value;

    // Parent gewählt → Child + Menge zurücksetzen
    if (field === 'parent_id') {
      updated[index].child_id = '';
      updated[index].menge_verfuegbar = 0;
      updated[index].menge = '';
      setPositionen(updated);
      return;
    }

    // Child gewählt → Verfügbarkeit holen
    if (field === 'child_id') {
      const childId = Number(value);
      updated[index].child_id = childId;
      updated[index].menge = '';
      updated[index].menge_verfuegbar = 0;
      setPositionen(updated);

      // Wenn Quelle ein Lagerort ist, Verfügbarkeit abfragen
      if (quelleTyp === 'LAGERORT' && quelleId) {
        try {
          const res = await fetchApi(
  `/api/view/verfuegbar?artikel_id=${childId}&lagerort_id=${quelleId}`
);

          const json = await res.json();

          const verfuegbar = json.verfuegbar ?? 0;

          setPositionen(prev => {
            const neu = [...prev];
            if (neu[index]) {
              neu[index].menge_verfuegbar = verfuegbar;
              neu[index].menge = '';
            }
            return neu;
          });
        } catch (err) {
          console.error('Fehler beim Laden der Verfügbarkeit:', err);
          setPositionen(prev => {
            const neu = [...prev];
            if (neu[index]) {
              neu[index].menge_verfuegbar = 0;
              neu[index].menge = '';
            }
            return neu;
          });
        }
      } else {
        // Keine Lagerort-Quelle → praktisch unbegrenzt
        setPositionen(prev => {
          const neu = [...prev];
          if (neu[index]) {
            neu[index].menge_verfuegbar = 999999;
            neu[index].menge = '';
          }
          return neu;
        });
      }

      return;
    }

    // Menge geändert
    if (field === 'menge') {
      const neueMenge = Number(value);
      updated[index].menge = neueMenge;
      setPositionen(updated);
      return;
    }

    setPositionen(updated);
  };

  const handleAddPosition = () => {
    setPositionen(prev => [
      ...prev,
      { parent_id: '', child_id: '', menge: '', menge_verfuegbar: 0 }
    ]);
  };

  const handleRemovePosition = (index: number) => {
    setPositionen(prev => prev.filter((_, i) => i !== index));
  };

  // ========================================================
  // ABSENDEN
  // ========================================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validierung: bei Quelle Lagerort darf Menge nie > verfügbare Menge sein
    if (quelleTyp === 'LAGERORT') {
      for (const pos of positionen) {
        if (!pos.child_id || !pos.menge) continue;
        const m = Number(pos.menge);
        if (m > pos.menge_verfuegbar) {
          alert(
            `Menge ${m} überschreitet die verfügbare Menge (${pos.menge_verfuegbar}).`
          );
          return;
        }
      }
    }

    const payload = {
      quelle_typ: quelleTyp,
      quelle_id: quelleId,
      ziel_typ: zielTyp,
      ziel_id: zielId,
      bemerkung,
      positionen: positionen.map(p => ({
        artikel_id: p.child_id,
        menge: p.menge
      }))
    };

    const res = await fetchApi('/api/lieferschein', {
  method: 'POST',
  body: JSON.stringify(payload)
});

    const json = await res.json();

    if (res.ok) {
  alert(`Lieferschein erstellt (ID: ${json.lieferschein_id})`);
      setQuelleTyp('');
      setQuelleId(null);
      setZielTyp('');
      setZielId(null);
      setBemerkung('');
      setPositionen([
        { parent_id: '', child_id: '', menge: '', menge_verfuegbar: 0 }
      ]);
    } else {
  alert(
    res.status === 401
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : (json.error || 'Fehler')
  );
}
  };

  if (!meta) return <div>Lade…</div>;

  // ========================================================
  // UI
  // ========================================================
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Lieferschein erstellen
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* ===================== QUELLE ===================== */}
          <Grid item xs={12}>
            <Typography variant="h6">Quelle</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Quelle-Typ"
              fullWidth
              value={quelleTyp}
              onChange={(e) => {
                setQuelleTyp(e.target.value);
                setQuelleId(null);
                // Verfügbarkeiten zurücksetzen
                setPositionen(prev =>
                  prev.map(p => ({
                    ...p,
                    menge_verfuegbar: 0,
                    menge: '',
                  }))
                );
              }}
              required
            >
              {meta.quelle_typ.map(opt => (
                <MenuItem key={opt.code} value={opt.code}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {quelleTyp === 'LAGERORT' &&
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Lagerort"
                fullWidth
                value={quelleId || ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setQuelleId(id);
                  // Verfügbarkeiten zurücksetzen, weil anderer Lagerort
                  setPositionen(prev =>
                    prev.map(p => ({
                      ...p,
                      menge_verfuegbar: 0,
                      menge: '',
                    }))
                  );
                }}
                required
              >
                {meta.lagerorte.map(lo => (
                  <MenuItem key={lo.id} value={lo.id}>
                    {lo.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          }

          {quelleTyp === 'LIEFERANT' &&
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Lieferant"
                fullWidth
                value={quelleId || ''}
                onChange={(e) => setQuelleId(Number(e.target.value))}
                required
              >
                {meta.lieferanten.map(li => (
                  <MenuItem key={li.id} value={li.id}>
                    {li.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          }

          {/* ===================== ZIEL ===================== */}
          <Grid item xs={12}>
            <Typography variant="h6">Ziel</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Ziel-Typ"
              fullWidth
              value={zielTyp}
              onChange={(e) => {
                setZielTyp(e.target.value);
                setZielId(null);
              }}
              required
            >
              {meta.ziel_typ.map(opt => (
                <MenuItem key={opt.code} value={opt.code}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {zielTyp === 'LAGERORT' &&
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Lagerort"
                fullWidth
                value={zielId || ''}
                onChange={(e) => setZielId(Number(e.target.value))}
                required
              >
                {meta.lagerorte.map(lo => (
                  <MenuItem key={lo.id} value={lo.id}>
                    {lo.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          }

          {/* ===================== BEMERKUNG ===================== */}
          <Grid item xs={12}>
            <TextField
              label="Bemerkung"
              fullWidth
              multiline
              rows={2}
              value={bemerkung}
              onChange={(e) => setBemerkung(e.target.value)}
              placeholder="Optional: Hinweis eingeben"
            />
          </Grid>

          {/* ===================== POSITIONEN ===================== */}
          <Grid item xs={12}>
            <Typography variant="h6">Positionen</Typography>
          </Grid>

          {positionen.map((pos, index) => (
            <React.Fragment key={index}>

              {/* Parent-Auswahl */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Produkt (Parent)"
                  value={pos.parent_id}
                  onChange={(e) =>
                    handlePositionsChange(index, 'parent_id', Number(e.target.value))
                  }
                  required
                >
                  {parents.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.sku} - {p.bezeichnung}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Child-Auswahl */}
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  fullWidth
                  label="Variante (Child)"
                  value={pos.child_id}
                  onChange={(e) =>
                    handlePositionsChange(index, 'child_id', Number(e.target.value))
                  }
                  required
                  disabled={!pos.parent_id}
                >
                  {childs
                    .filter(c => c.parent_id === pos.parent_id)
                    .map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.sku} - {c.bezeichnung}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              {/* Verfügbarkeit */}
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Verfügbar"
                  fullWidth
                  value={
                    quelleTyp === 'LAGERORT'
                      ? pos.menge_verfuegbar
                      : '∞'
                  }
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* Menge */}
              <Grid item xs={12} sm={1}>
                <TextField
                  type="number"
                  label="Menge"
                  fullWidth
                  required
                  value={pos.menge}
                  onChange={(e) =>
                    handlePositionsChange(index, 'menge', Number(e.target.value))
                  }
                  inputProps={{
                    min: 1,
                    max: quelleTyp === 'LAGERORT'
                      ? pos.menge_verfuegbar
                      : undefined
                  }}
                  disabled={!pos.child_id}
                />
              </Grid>

              {/* Entfernen */}
              <Grid item xs={12} sm={12}>
                <IconButton
                  onClick={() => handleRemovePosition(index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>

            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Button
              startIcon={<AddCircleIcon />}
              onClick={handleAddPosition}
              variant="outlined"
            >
              Weitere Position
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button type="submit" variant="contained">
              Lieferschein erstellen
            </Button>
          </Grid>

        </Grid>
      </Box>
    </Container>
  );
}
